import React, { Component } from 'react';
import HttpRunner from '../query';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';
import miscUtils from '../../../utils/miscUtils';

class DocIpv4Entity extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSample: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSample: perms.checkEndpoint(urlHandler.API_ENDPOINT_V2_ENRICH)
        }) 
    }

    render() {
        return (
            <div className="bucket" id="top">
                <div>
                    <h3 id="ip" className="heading-1">IPV4</h3>
                    <div className="description"> The extraction of a Internet Protocol version 4 (IPv4) address is based
                        upon the internet protocol specifications (RFC 791) and extracts addresses that
                        are in dotted decimal, dotted hexadecimal, or dotted octal notation. An IPv4 entity
                        consists of four octets (of the same notation), each separated by a dot (.), and may
                        contain a port, separated by a colon from the fourth octet. A decimal octet consists
                        of a base-10 value from 0-255. A hexadecimal octet consists of a base-16 value from
                        00- FF and must have a preceding “x”, “0x”, or “\0x”. An octal octet must have a
                        preceding zero and contain a base-8 value from 0000-0377.
                    </div>
                    <br/>
                    <div className="example-content">{'<'}octet-1{'>'}.{'<'}octet-2{'>'}.{'<'}octet-3{'>'}.{'<'}octet-4{'>'}:{'<'}port{'>'}</div>
                    <code className="code-snippet">
                        <span className="line">192.168.2.1:8080 </span>
                        <span className="line">0x16.0x65.0xF1.0x99 </span>
                        <span className="line">026.0145.037.0231 </span>
                    </code>

                    <div className="description">
                        Other forms of extracted IPv4 address that slightly differ from the standards
                        are those whose octets are separated by dots (.) with surrounding whitespace.
                    </div>

                    <code className="code-snippet">
                        <span className="line">192. 168. 2. 1</span>
                        <span className="line">0x16 . 0x65 . 0xF1 . 0x99 </span>
                        <span className="line">026.\n0145.\n037.\n0231 </span>
                    </code>

                    <div className="description">
                        All IPv4 entities expressed in dotted hexadecimal or dotted octal notation
                        will be standardized to a dotted decimal value.
                    </div>

                    <div className="description">
                        An extracted IPv4 address will be represented by the following attributes:
                    </div>

                    <table className="table-wrapper">
                        <thead className="table-header">
                        <tr>
                            <th>Attribute </th>
                            <th>Meaning </th>
                            <th>Type </th>
                            <th>Constraints </th>
                            <th>Notes </th>
                        </tr>
                        </thead>
                        <tbody className="table-body">
                        <tr>
                            <td className="attribute">port</td>
                            <td className="meaning">The port number associated with the IP address</td>
                            <td className="type">Number</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Must be a valid base-10 value. Must be separated from the fourth octect by a colon (:)</td>
                        </tr>
                        <tr>
                            <td className="attribute">continentLabel</td>
                            <td className="meaning">The enclosing continent name of the IP address</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">countryLabel</td>
                            <td className="meaning">The enclosing country name of the IP address</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">countryIso</td>
                            <td className="meaning">The {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/ISO_3166-1','ISO 3166-1')} country code for the country associated with the address</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">The two character {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/ISO_3166-1','ISO 3166-1')} country code</td>
                        </tr>
                        <tr>
                            <td className="attribute">admin1Label</td>
                            <td className="meaning">The enclosing first level region of the IP address</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">admin1Iso</td>
                            <td className="meaning">The {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/ISO_3166-2', 'ISO 3166-2')} code for the first level region associated with the address</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">A string of up to 3 characters containing the region portion of the {miscUtils.createExternalLink('https://en.wikipedia.org/wiki/ISO_3166-2','ISO 3166-2')} code</td>
                        </tr>
                        <tr>
                            <td className="attribute">cityLabel</td>
                            <td className="meaning">The city name associated with the location of the IP address</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">postalCode</td>
                            <td className="meaning">The postal code associated with the IP address</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Available for some IP addresses in Australia, Canada, France, Germany, Italy, Spain, Switzerland, United Kingdom and the US</td>
                        </tr>
                        <tr>
                            <td className="attribute">latitude</td>
                            <td className="meaning">The approximate latitude of the location associated with the network</td>
                            <td className="type">Double</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Value is approximate</td>
                        </tr>
                        <tr>
                            <td className="attribute">longitude</td>
                            <td className="meaning">The approximate longitude of the location associated with the network</td>
                            <td className="type">Double</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Value is approximate</td>
                        </tr>
                        <tr>
                            <td className="attribute">accuracyRadius</td>
                            <td className="meaning">The radius in kilometers around the specified location where the IP address is likely to be</td>
                            <td className="type">Integer</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        <tr>
                            <td className="attribute">metroCode</td>
                            <td className="meaning">The metro code associated with the IP address</td>
                            <td className="type">Integer</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Only available for networks in the US</td>
                        </tr>
                        <tr>
                            <td className="attribute">timeZone</td>
                            <td className="meaning">The time zone associated with the location of the IP address</td>
                            <td className="type">String</td>
                            <td className="constraints">Optional</td>
                            <td className="notes">Specified by the {miscUtils.createExternalLink('https://www.iana.org/time-zones','IANA Time Zone Database')}. ex. "America/New_York"</td>
                        </tr>
                        <tr>
                            <td className="attribute">legitimateProxy</td>
                            <td className="meaning">Suggests whether the IP address is a virus based proxy and not a dedicated proxy server</td>
                            <td className="type">Boolean</td>
                            <td className="constraints">Optional</td>
                            <td className="notes"></td>
                        </tr>
                        </tbody>
                    </table>

                    <h4 className="heading-2">Examples</h4>
                    <div className="example-content">...can ping the servers over at qmachine-1 on 0x16.0x65.0xF1.0x99 or qmachine-2 on 026.0145.037.0231 
                        or run locally on 127.0.0.1:8080 with the exception that...</div>
                    <HttpRunner example="ip1" 
                        query="enrich"
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_IPV41) : null}
                    />
                    <br/>
                    <div className="example-content">The IP 144. 0. 241. 198 has just been banned by Fail2Ban after 5 attempts.</div>
                    <HttpRunner example="ip2" 
                        query="enrich"
                        response={!this.state.dynamicSample ? JSON.stringify(examples.EXAMPLE_RESPONSE_IPV42) : null}
                    />
                </div>
            </div>
        );
    }
}

export default DocIpv4Entity;